import React, { useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';

import {
  Avatar,
  Box,
  Button,
  Divider,
  Hidden,
  lighten,
  List,
  ListItem,
  ListItemText,
  Popover,
  Typography,
  useTheme
} from '@mui/material';

import { styled } from '@mui/material/styles';
import ExpandMoreTwoToneIcon from '@mui/icons-material/ExpandMoreTwoTone';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LockOpenTwoToneIcon from '@mui/icons-material/LockOpenTwoTone';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useAuth } from 'src/contexts/AuthContext';

const UserBoxButton = styled(Button)(
  ({ theme }) => `
        padding-left: ${theme.spacing(1)};
        padding-right: ${theme.spacing(1)};
`
);

const MenuUserBox = styled(Box)(
  ({ theme }) => `
        background: ${theme.colors.alpha.black[5]};
        padding: ${theme.spacing(2)};
`
);

const UserBoxText = styled(Box)(
  ({ theme }) => `
        text-align: left;
        padding-left: ${theme.spacing(1)};
`
);

const UserBoxLabel = styled(Typography)(
  ({ theme }) => `
        font-weight: ${theme.typography.fontWeightBold};
        color: ${theme.palette.text.primary};
        display: block;
`
);

const UserBoxDescription = styled(Typography)(
  ({ theme }) => `
        color: ${theme.palette.text.secondary};
`
);

// Map role IDs to readable role names
const roleNames: Record<string, string> = {
  // Roles from V9 migration
  ADMIN: 'Administrator',
  DISPATCHER: 'Dispatcher',
  RTU: 'RTU', // Or 'Railway Undertaking'
  HANDLING_COMPANY: 'Handling Company',
  // Roles from setup_eius.sql (and observed in JWT)
  SYSTEM_ADMIN: 'System Administrator',
  EIU_ADMIN: 'Infrastructure Provider Administrator',
  EVU_ADMIN: 'Railway Company Administrator',
  USER: 'Regular User'
  // Note: 'Infrastructure Provider Administrator' from previous map is covered by EIU_ADMIN
};

function HeaderUserbox() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const theme = useTheme();
  const [language, setLanguage] = useState<string>(i18n.language || 'de');

  // Get the user's name from the profile or displayName
  const userName = user?.userProfile?.name || user?.displayName || 'User';

  // Handle roles array - Display the first role found, map its name
  const firstRoleKey =
    user?.roles && user.roles.length > 0 ? user.roles[0] : undefined;
  const roleDisplay = firstRoleKey
    ? roleNames[firstRoleKey] || firstRoleKey
    : 'Guest';

  const initials = userName
    .split(' ')
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase();

  const ref = useRef<HTMLButtonElement>(null);
  const [isOpen, setOpen] = useState<boolean>(false);

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleLogout = (): void => {
    handleClose();

    // Manually clear all auth-related localStorage items
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    // Also clear sessionStorage items
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');

    // Then call the auth context logout
    logout();

    // Use replace instead of push for cleaner navigation
    navigate('/login', { replace: true });
  };

  const handleLanguageChange = (event: SelectChangeEvent<string>) => {
    const lang = event.target.value as string;
    setLanguage(lang);
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
  };

  React.useEffect(() => {
    const storedLang = localStorage.getItem('language');
    if (storedLang && storedLang !== i18n.language) {
      i18n.changeLanguage(storedLang);
      setLanguage(storedLang);
    }
  }, [i18n]);

  // Generate avatar background color using Accso colors
  const getUserColor = (): string => {
    // Use username (email) as the stable identifier for color generation
    const stableIdentifier =
      user?.username || user?.userProfile?.email || 'default';

    let hash = 0;
    for (let i = 0; i < stableIdentifier.length; i++) {
      hash = stableIdentifier.charCodeAt(i) + ((hash << 5) - hash);
    }

    // Use Accso colors for avatar backgrounds
    const accsoColors = [
      theme.colors.primary.main, // Technogrün
      theme.colors.secondary.main, // Violett
      theme.colors.info.main, // Also Violett
      theme.colors.warning.main, // Orange
      theme.colors.primary.dark // Darker Technogrün
    ];

    return accsoColors[Math.abs(hash) % accsoColors.length];
  };

  return (
    <>
      <UserBoxButton color="secondary" ref={ref} onClick={handleOpen}>
        <Avatar
          variant="rounded"
          sx={{
            bgcolor: getUserColor(),
            color: 'white',
            fontWeight: 'bold'
          }}
        >
          {initials}
        </Avatar>
        <Hidden mdDown>
          <UserBoxText>
            <UserBoxLabel variant="body1">{userName}</UserBoxLabel>
            <UserBoxDescription variant="body2">
              {roleDisplay}
            </UserBoxDescription>
          </UserBoxText>
        </Hidden>
        <Hidden smDown>
          <ExpandMoreTwoToneIcon
            sx={{ ml: 1, color: theme.palette.text.primary }}
          />
        </Hidden>
      </UserBoxButton>
      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <MenuUserBox sx={{ minWidth: 210 }} display="flex">
          <Avatar
            variant="rounded"
            sx={{
              bgcolor: getUserColor(),
              color: 'white',
              fontWeight: 'bold'
            }}
          >
            {initials}
          </Avatar>
          <UserBoxText>
            <UserBoxLabel variant="body1">{userName}</UserBoxLabel>
            <UserBoxDescription variant="body2">
              {roleDisplay}
            </UserBoxDescription>
          </UserBoxText>
        </MenuUserBox>
        <Divider sx={{ mb: 0 }} />
        <List sx={{ p: 1 }} component="nav">
          <ListItem button to="/management/profile" component={NavLink}>
            <AccountCircleIcon fontSize="small" />
            <ListItemText primary={t('accountMenu.profile')} />
          </ListItem>
          <ListItem button to="/user/preferences" component={NavLink}>
            <SettingsIcon fontSize="small" />
            <ListItemText primary={t('accountMenu.preferences')} />
          </ListItem>
          <ListItem button to="/notifications" component={NavLink}>
            <NotificationsIcon fontSize="small" />
            <ListItemText primary={t('accountMenu.notifications')} />
          </ListItem>
          <ListItem button to="/help" component={NavLink}>
            <HelpOutlineIcon fontSize="small" />
            <ListItemText primary={t('accountMenu.help')} />
          </ListItem>
        </List>
        <Box sx={{ m: 2, mt: 0, mb: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {t('general.language', 'Language')}
          </Typography>
          <Select
            value={language}
            onChange={handleLanguageChange}
            size="small"
            fullWidth
          >
            <MenuItem value="de">{t('general.german', 'Deutsch')}</MenuItem>
            <MenuItem value="en">{t('general.english', 'English')}</MenuItem>
          </Select>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: 'block' }}
          >
            Version v{import.meta.env.VITE_APP_VERSION}
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ m: 1 }}>
          <Button
            color="primary"
            fullWidth
            onClick={handleLogout}
            sx={{
              background: theme.colors.gradients.blue1,
              color: 'white',
              '&:hover': {
                background: theme.colors.gradients.blue3
              }
            }}
          >
            <LockOpenTwoToneIcon sx={{ mr: 1 }} />
            {t('general.sign_out', 'Sign out')}
          </Button>
        </Box>
      </Popover>
    </>
  );
}

export default HeaderUserbox;
